import { DbProduct } from '@/hooks/useMenuData';
import { useCurrency } from '@/hooks/useCurrency';
import JustEatButton from '@/components/feature/JustEatButton';

interface MenuProductPanelProps {
  item: DbProduct | null;
}

const placeholderImg = 'https://readdy.ai/api/search-image?query=elegant%20empty%20restaurant%20table%20with%20soft%20warm%20candlelight%2C%20minimalist%20fine%20dining%20atmosphere%2C%20blurred%20bokeh%20background%2C%20warm%20amber%20tones%2C%20sophisticated%20ambiance%2C%20no%20food%20on%20table&width=600&height=700&seq=menu-empty-panel&orientation=portrait';

const MenuProductPanel = ({ item }: MenuProductPanelProps) => {
  const { format } = useCurrency();

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={placeholderImg}
            alt="Select a dish"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-white/30" />
        </div>
        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="w-20 h-20 flex items-center justify-center bg-amber-50 border-2 border-amber-100 rounded-3xl">
            <i className="ri-restaurant-2-line text-3xl text-amber-400"></i>
          </div>
          <div>
            <h3 className="text-stone-700 font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              Gericht auswählen
            </h3>
            <p className="text-stone-400 text-sm mt-2 leading-relaxed max-w-xs">
              Klicken Sie auf ein Gericht, um Details zu sehen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="relative w-full aspect-square flex-shrink-0 bg-stone-100 overflow-hidden">
        <img
          src={item.image_url || placeholderImg}
          alt={item.name_de}
          className="w-full h-full object-contain object-center"
        />
      </div>

      <div className="flex-1 p-6 flex flex-col gap-5">
        <h2
          className="text-stone-900 text-2xl font-bold"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {item.name_de}
        </h2>
        {item.description_de && (
          <p className="text-stone-500 text-sm leading-relaxed">{item.description_de}</p>
        )}

        <div className="flex items-center justify-between py-3 px-4 bg-stone-50 rounded-xl border border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-price-tag-3-line text-stone-400 text-sm"></i>
            </div>
            <span className="text-stone-600 text-sm font-medium">Preis</span>
          </div>
          <span className="text-stone-900 font-bold text-sm">{format(item.price)}</span>
        </div>

        {item.extras.length > 0 && (
          <div>
            <h4 className="text-stone-800 font-semibold text-sm mb-3">Mögliche Extras</h4>
            <div className="space-y-2">
              {item.extras.map((extra) => (
                <div
                  key={extra.id}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-stone-100 bg-white text-sm"
                >
                  <span className="font-medium text-stone-700">{extra.name_de}</span>
                  {extra.price > 0 && (
                    <span className="text-xs font-semibold text-stone-400">+{format(extra.price)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1" />

        <JustEatButton className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2">
          <i className="ri-external-link-line text-base"></i>
          <span>Auf Just Eat bestellen</span>
        </JustEatButton>
      </div>
    </div>
  );
};

export default MenuProductPanel;
